import { useCallback, useEffect, useState } from "react";
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	ModalCloseButton,
	FormControl,
	FormErrorMessage,
	Input,
	Checkbox,
	Stack,
	Text,
	Alert,
	AlertIcon,
	Switch,
} from "@chakra-ui/react";

import { Button } from "newui/components/Button";
import type { RfxQuoteTable, SummaryTableConfig } from "../../Submission/types/quote.type";
import { validateSummaryTableValueColumns } from "../../api";
import type { QuoteResourceType } from "../../api/procurer-quotes-api";
import { InputWithLabel } from "newui/components/ui/InputWithLabel";
import { FormLabel } from "newui/components/ui/FormLabel";
import { SummaryTablePreview } from "./SummaryTablePreview";

export interface CreateSummaryTableModalProps {
	availableQuoteTables: RfxQuoteTable[];
	editingTable?: SummaryTableConfig | null;
	isOpen: boolean;
	/**
	 * Resource the summary table belongs to. Determines which API route the
	 * validate call hits (`rfx/{id}` vs `rfx-template/{id}`). Defaults to
	 * `"rfx"` for backward compatibility with the live-quote flow.
	 */
	resourceType?: QuoteResourceType;
	rfxId: number;
	onClose: () => void;
	onSubmit: (name: string, quoteTableIds: number[], valueColumnNames: string[], includeColumnTotals: boolean) => void;
}

const CreateSummaryTableModal: React.FC<CreateSummaryTableModalProps> = ({
	availableQuoteTables,
	editingTable,
	isOpen,
	onClose,
	onSubmit,
	resourceType = "rfx",
	rfxId,
}) => {
	const [name, setName] = useState("");
	const [nameError, setNameError] = useState("");
	const [selectedTableIds, setSelectedTableIds] = useState<number[]>([]);
	const [selectedValueColumns, setSelectedValueColumns] = useState<string[]>([]);
	const [includeColumnTotals, setIncludeColumnTotals] = useState(false);
	const [validationErrors, setValidationErrors] = useState<string[]>([]);
	const [isValidating, setIsValidating] = useState(false);

	const isEditing = editingTable != null;

	// Find value columns that match across selected tables
	const { matchingValueColumns, hasNoMatches } = (() => {
		const selectedTables = availableQuoteTables.filter((qt) =>
			selectedTableIds.includes(qt.id)
		);
		if (selectedTables.length < 2) {
			// With fewer than 2 tables, show all value columns from selected tables
			const cols = new Set<string>();
			selectedTables.forEach((qt) => {
				qt.columns
					?.filter((c) => c.columnType === "response_value" || c.columnType === "saving")
					.forEach((c) => cols.add(c.name));
			});
			return { matchingValueColumns: Array.from(cols), hasNoMatches: false };
		}

		// Count how many selected tables each value column name appears in
		const columnCounts: Record<string, number> = {};
		selectedTables.forEach((qt) => {
			const seen = new Set<string>();
			qt.columns
				?.filter((c) => c.columnType === "response_value" || c.columnType === "saving")
				.forEach((c) => {
					if (!seen.has(c.name)) {
						seen.add(c.name);
						columnCounts[c.name] = (columnCounts[c.name] || 0) + 1;
					}
				});
		});

		const matching = Object.entries(columnCounts)
			.filter(([, count]) => count >= 2)
			.map(([colName]) => colName);

		return {
			matchingValueColumns: matching,
			hasNoMatches: matching.length === 0,
		};
	})();

	useEffect(() => {
		if (isOpen) {
			if (editingTable) {
				setName(editingTable.name);
				setSelectedTableIds([...editingTable.quote_table_ids]);
				setSelectedValueColumns([...editingTable.value_column_names]);
				setIncludeColumnTotals(editingTable.include_column_totals ?? false);
			} else {
				setName("");
				setSelectedTableIds([]);
				setSelectedValueColumns([]);
				setIncludeColumnTotals(false);
			}
			setNameError("");
			setValidationErrors([]);
		}
	}, [isOpen, editingTable]);

	const handleTableToggle = useCallback(
		(tableId: number) => {
			setSelectedTableIds((prev) =>
				prev.includes(tableId)
					? prev.filter((id) => id !== tableId)
					: [...prev, tableId]
			);
			setSelectedValueColumns([]);
			setValidationErrors([]);
		},
		[]
	);

	const handleValueColumnToggle = useCallback(
		(columnName: string) => {
			setSelectedValueColumns((prev) =>
				prev.includes(columnName)
					? prev.filter((c) => c !== columnName)
					: [...prev, columnName]
			);
			setValidationErrors([]);
		},
		[]
	);

	// --- Select All helpers --------------------------------------------------
	const allTableIds = availableQuoteTables.map((qt) => qt.id);
	const allTablesSelected =
		allTableIds.length > 0 && selectedTableIds.length === allTableIds.length;
	const someTablesSelected =
		selectedTableIds.length > 0 && !allTablesSelected;
	const handleToggleAllTables = useCallback(() => {
		setSelectedTableIds(allTablesSelected ? [] : allTableIds);
		setSelectedValueColumns([]);
		setValidationErrors([]);
	}, [allTablesSelected, allTableIds]);

	const allValueColumnsSelected =
		matchingValueColumns.length > 0 &&
		selectedValueColumns.length === matchingValueColumns.length;
	const someValueColumnsSelected =
		selectedValueColumns.length > 0 && !allValueColumnsSelected;
	const handleToggleAllValueColumns = useCallback(() => {
		setSelectedValueColumns(
			allValueColumnsSelected ? [] : [...matchingValueColumns]
		);
		setValidationErrors([]);
	}, [allValueColumnsSelected, matchingValueColumns]);

	// --- Preview-pane data ---------------------------------------------------
	const previewTables = availableQuoteTables.filter((qt) =>
		selectedTableIds.includes(qt.id)
	);
	const previewColumns = selectedValueColumns;

	// --- Name validation -----------------------------------------------------
	const validateName = useCallback((value: string): string => {
		if (!value.trim()) return "Summary table name is required";
		return "";
	}, []);
	const handleNameBlur = useCallback(() => {
		setNameError(validateName(name));
	}, [name, validateName]);

	const handleSubmit = useCallback(async () => {
		if (!name.trim()) {
			setNameError("Summary table name is required");
			return;
		}
		setNameError("");

		if (selectedTableIds.length === 0) {
			setValidationErrors(["Please select at least one quote table"]);
			return;
		}

		if (selectedValueColumns.length === 0) {
			setValidationErrors(["Please select at least one value column"]);
			return;
		}

		setIsValidating(true);
		try {
			const result = await validateSummaryTableValueColumns(
				rfxId,
				selectedTableIds,
				selectedValueColumns,
				resourceType
			);
			if (!result.valid) {
				setValidationErrors(result.errors);
				return;
			}
			onSubmit(name.trim(), selectedTableIds, selectedValueColumns, includeColumnTotals);
			onClose();
		} catch {
			setValidationErrors(["Failed to validate configuration. Please try again."]);
		} finally {
			setIsValidating(false);
		}
	}, [name, selectedTableIds, selectedValueColumns, includeColumnTotals, rfxId, resourceType, onSubmit, onClose]);

	const quoteTableNamesById: Record<number, string> = Object.fromEntries(
		availableQuoteTables.map((qt) => [qt.id, qt.name])
	);

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="6xl">
			<ModalOverlay />
			<ModalContent data-testid="create-summary-table-modal" maxW="min(1100px, 95vw)">
				<ModalHeader>{isEditing ? "Edit Summary Table" : "Create Summary Table"}</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
						{/* ---------- LEFT: Form ---------- */}
						<div>
							<InputWithLabel label="Summary Table Name" hasError={!!nameError} className="mb-4" required
								value={name}
								onChange={(e) => {
									setName(e.target.value);
									if (nameError) {
										setNameError("");
									}
								}}
								onBlur={handleNameBlur}
								placeholder="e.g. Overall Summary"
								data-testid="summary-table-name-input"
								errorText={nameError || "Name for summary table is required"}
							/>

							<FormControl className="mb-4">
								<div className="flex items-center justify-between">
									<FormLabel className="text-sm font-medium text-gray-700">
										Select Quote Tables
									</FormLabel>
									{availableQuoteTables.length > 0 && (
										<Checkbox
											size="sm"
											isChecked={allTablesSelected}
											isIndeterminate={someTablesSelected}
											onChange={handleToggleAllTables}
											data-testid="select-all-tables"
										>
											<Text className="text-xs text-gray-600">Select All</Text>
										</Checkbox>
									)}
								</div>
								<Text className="mb-2 text-xs text-gray-500">
									Select which quote tables to include as rows
								</Text>
								<Stack spacing={2}>
									{availableQuoteTables.map((qt) => (
										<Checkbox
											key={qt.id}
											isChecked={selectedTableIds.includes(qt.id)}
											onChange={() => handleTableToggle(qt.id)}
											data-testid={`select-table-${qt.id}`}
										>
											<Text className="text-sm">{qt.name}</Text>
										</Checkbox>
									))}
								</Stack>
							</FormControl>

							{selectedTableIds.length >= 2 && hasNoMatches && (
								<Alert
									status="warning"
									className="mb-4 rounded-md"
									data-testid="no-matching-columns-warning"
								>
									<AlertIcon />
									<div>
										<Text className="text-sm font-medium">
											No matching cost columns found
										</Text>
										<Text className="text-xs text-gray-600">
											To create a summary table, the selected quote tables need at least one
											value column with the same name (e.g. &quot;Rate&quot;). Add matching value
											column names to your quote tables first.
										</Text>
									</div>
								</Alert>
							)}

							{matchingValueColumns.length > 0 && (
								<FormControl className="mb-4">
									<div className="flex items-center justify-between">
										<FormLabel className="text-sm font-medium text-gray-700">
											Value Columns
										</FormLabel>
										<Checkbox
											size="sm"
											isChecked={allValueColumnsSelected}
											isIndeterminate={someValueColumnsSelected}
											onChange={handleToggleAllValueColumns}
											data-testid="select-all-value-columns"
										>
											<Text className="text-xs text-gray-600">Select All</Text>
										</Checkbox>
									</div>
									<Text className="mb-2 text-xs text-gray-500">
										Select which value columns to use in the summary
									</Text>
									<Stack spacing={2}>
										{matchingValueColumns.map((col) => (
											<Checkbox
												key={col}
												isChecked={selectedValueColumns.includes(col)}
												onChange={() => handleValueColumnToggle(col)}
												data-testid={`select-value-column-${col}`}
											>
												<Text className="text-sm">{col}</Text>
											</Checkbox>
										))}
									</Stack>
								</FormControl>
							)}

							<FormControl className="mb-4 flex items-center justify-between">
								<FormLabel className="mb-0 text-sm font-medium text-gray-700">
									Include Column Totals
								</FormLabel>
								<Switch
									isChecked={includeColumnTotals}
									onChange={() => setIncludeColumnTotals((prev) => !prev)}
									data-testid="include-column-totals-switch"
								/>
							</FormControl>
						</div>

						{/* ---------- RIGHT: Preview ---------- */}
						<div className="md:sticky md:top-0">
							<Text className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
								Preview
							</Text>
							<SummaryTablePreview
								summaryTableName={name}
								quoteTableIds={selectedTableIds}
								quoteTableNames={quoteTableNamesById}
								categoryNames={previewColumns}
								includeColumnTotals={includeColumnTotals}
							/>
						</div>
					</div>

					{validationErrors.length > 0 && (
						<Alert
							status="error"
							className="mt-3 rounded-md"
							data-testid="validation-errors"
						>
							<AlertIcon />
							<div>
								{validationErrors.map((err) => (
									<Text key={err} className="text-sm">
										{err}
									</Text>
								))}
							</div>
						</Alert>
					)}
				</ModalBody>
				<ModalFooter>
					<Button
						btntype="secondary"
						onClick={onClose}
						className="mr-3"
						data-testid="cancel-summary-table"
					>
						Cancel
					</Button>
					<Button
						btntype="primary"
						onClick={handleSubmit}
						disabled={isValidating}
						data-testid="submit-summary-table"
					>
						{isValidating ? "Validating..." : isEditing ? "Save" : "Create"}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export { CreateSummaryTableModal };
